import { FC, useState, useEffect } from 'react';
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  TFinancePrimary,
  TTransactionType,
  TStatus,
} from '../../../../../apis/finance/finance.interfaces';
import {
  useFinanceState,
  useFinanceActions,
} from '../../contexts/finance.context';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import { useFinanceService } from 'src/apis/finance/useFinanceService';
import { isValid } from 'date-fns';

enum EFinanceView {
  LIST = 'list',
  DETAILS = 'details',
}

interface FinanceListGridProps {
  isLoading?: boolean;
}

const FinanceListGrid: FC<FinanceListGridProps> = () => {
  const { translateText } = useTranslation();
  const { financeList, financeAppState } = useFinanceState();
  const { getFinanceRecordById } = useFinanceService();
  const {
    setSelectedFinanceItem,
    setSelectedFinanceItemId,
    setIsManageFinanceItemOpen,
    setIsCreateMode,
    setIsEditMode,
  } = useFinanceActions();
  const [transactionType, setTransactionType] = useState<TTransactionType>(
    TTransactionType.ALL
  );
  const [viewType, setViewType] = useState<EFinanceView>(EFinanceView.LIST);
  const [filteredFinanceList, setFilteredFinanceList] =
    useState<TFinancePrimary[]>(financeList);

  useEffect(() => {
    setFilteredFinanceList(
      financeList.filter(
        (finance) =>
          transactionType === TTransactionType.ALL ||
          finance.type === transactionType
      )
    );
  }, [transactionType, financeList]);

  // Sync viewType with context state
  useEffect(() => {
    if (financeAppState.selectedFinanceItem) {
      setViewType(EFinanceView.DETAILS);
    } else if (!financeAppState.selectedFinanceItemId) {
      setViewType(EFinanceView.LIST);
    }
  }, [
    financeAppState.selectedFinanceItem,
    financeAppState.selectedFinanceItemId,
  ]);

  useEffect(() => {
    const getFinanceDetails = async (finId: string) => {
      const finDetails = await getFinanceRecordById(finId);
      setSelectedFinanceItem(finDetails || null);
    };
    if (financeAppState?.selectedFinanceItemId) {
      getFinanceDetails(financeAppState?.selectedFinanceItemId);
    }
    // eslint-disable-next-line
  }, [financeAppState?.selectedFinanceItemId]);

  const getFinanceGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'name',
        headerName: translateText('finance.list.columns.name'),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'amount',
        headerName: translateText('finance.list.columns.amount'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TFinancePrimary;
          return `${Intl.NumberFormat('en-IN', { style: 'currency', currency: data.currencyCode || data.currency || 'INR' }).format(data.amount)}`;
        },
      },
      {
        field: 'date',
        headerName: translateText('finance.list.columns.date'),
        width: 120,
        type: 'date',
        renderCell: (params) => {
          const columnValue = params.value as string;
          if (!isValid(new Date(columnValue))) return '-';
          try {
            return new Date(columnValue).toLocaleDateString();
          } catch {
            return '-';
          }
        },
      },
      {
        field: 'type',
        headerName: translateText('finance.list.columns.type'),
        width: 120,
      },
      {
        field: 'category',
        headerName: translateText('finance.list.columns.category'),
        width: 150,
      },
      {
        field: 'status',
        headerName: translateText('finance.list.columns.status'),
        width: 120,
        renderCell: (params) => {
          const status = params.value as TStatus;
          return (
            <Chip
              label={status}
              color={
                status === TStatus.PAID
                  ? 'success'
                  : status === TStatus.PARTIAL
                    ? 'warning'
                    : status === TStatus.UNPAID
                      ? 'error'
                      : 'default'
              }
              size="small"
            />
          );
        },
      },
      {
        field: 'paymentMode',
        headerName: translateText('finance.list.columns.paymentMode'),
        width: 150,
      },
      {
        field: 'tags',
        headerName: translateText('finance.list.columns.tags'),
        width: 200,
        renderCell: (params) => {
          const data = params.row as TFinancePrimary;
          return data.tags?.join(', ') || '-';
        },
      },
    ];
  };

  const handleRowClick = (params: any) => {
    if (viewType === EFinanceView.LIST) {
      const finance = financeList.find((f) => f.id === params.row.id);
      if (finance) {
        setSelectedFinanceItemId(finance.id);
        setViewType(EFinanceView.DETAILS);
      }
    }
  };

  const handleBackToList = () => {
    setViewType(EFinanceView.LIST);
    setSelectedFinanceItem(null);
    setSelectedFinanceItemId(null);
  };

  const handleAddTransaction = () => {
    setIsManageFinanceItemOpen(true);
    setIsCreateMode(true);
    setIsEditMode(false);
  };

  const handleEditTransaction = () => {
    setIsManageFinanceItemOpen(true);
    setIsCreateMode(false);
    setIsEditMode(true);
  };

  return (
    <Grid
      container
      spacing={0}
      gap={0}
      sx={{
        height: '100%',
        alignContent: 'flex-start',
      }}
      data-testid="finance-list-grid-container"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'start' },
          justifyContent: 'space-between',
          mb: { xs: 1, sm: 2 },
          gap: 1,
        }}
        data-testid="finance-list-header-container"
      >
        {viewType === EFinanceView.DETAILS &&
          financeAppState.selectedFinanceItem ? (
          <Box
            display="flex"
            alignItems="center"
            data-testid="finance-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToList}
              sx={{ mr: 2 }}
              data-testid="back-to-list-button"
            >
              {translateText('finance.list.backToList')}
            </Button>
            <Typography variant="h6" data-testid="detail-header-title">
              {financeAppState.selectedFinanceItem.basic_details.description ||
                translateText('finance.list.nA')}
            </Typography>
          </Box>
        ) : (
          <ToggleButtonGroup
            size="medium"
            color="primary"
            exclusive
            value={transactionType}
            onChange={(_event, value) => setTransactionType(value)}
            aria-label={translateText('finance.list.financeView')}
            sx={{ height: 'var(--tab-group-height)' }}
            data-testid="finance-view-toggle-button-group"
          >
            <ToggleButton
              value={TTransactionType.ALL}
              data-testid="finance-view-toggle-button-all"
            >
              {translateText('finance.list.filters.all')}
            </ToggleButton>
            <ToggleButton
              value={TTransactionType.INCOME}
              data-testid="finance-view-toggle-button-income"
            >
              {translateText('finance.list.filters.income')}
            </ToggleButton>
            <ToggleButton
              value={TTransactionType.EXPENSE}
              data-testid="finance-view-toggle-button-expense"
            >
              {translateText('finance.list.filters.expense')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {viewType === EFinanceView.LIST ? (
          <AddButton
            label={translateText('finance.list.addTransaction')}
            onClickHandler={handleAddTransaction}
            data-testid="add-transaction-button"
          />
        ) : viewType === EFinanceView.DETAILS ? (
          <EditButton
            label={translateText('finance.list.editTransaction')}
            onClickHandler={handleEditTransaction}
            data-testid="edit-transaction-button"
          />
        ) : null}
      </Grid>

      {viewType === EFinanceView.DETAILS &&
        financeAppState.selectedFinanceItem && (
          <Grid
            item
            xs={12}
            sx={{ my: 2, alignSelf: 'flex-start', width: '100%' }}
            data-testid="finance-detail-info-container"
          >
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5 }}
                  data-testid="transaction-details-title"
                >
                  {translateText('finance.list.transactionDetails')}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box
                  data-testid="transaction-details-fields-grid"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(4, 1fr)',
                    },
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'start',
                  }}
                >
                  <Box data-testid="detail-name" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.name')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .description || translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-amount" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.amount')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {
                        financeAppState.selectedFinanceItem.basic_details
                          .currency_code
                      }{' '}
                      {financeAppState.selectedFinanceItem.basic_details.amount.toFixed(
                        2
                      )}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-paid-amount" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.paidAmount')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .paid_amount
                        ? `${financeAppState.selectedFinanceItem.basic_details.currency_code} ${financeAppState.selectedFinanceItem.basic_details.paid_amount.toFixed(2)}`
                        : translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-date" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.date')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.action.created_at
                        ? new Date(
                          financeAppState.selectedFinanceItem.action.created_at
                        ).toLocaleDateString()
                        : translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-due-date" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.dueDate')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .due_date
                        ? new Date(
                          financeAppState.selectedFinanceItem.basic_details.due_date
                        ).toLocaleDateString()
                        : translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-type" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.type')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details.type ||
                        translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-category" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.category')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-status" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.status')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          financeAppState.selectedFinanceItem.basic_details
                            .status || translateText('finance.list.nA')
                        }
                        color={
                          financeAppState.selectedFinanceItem.basic_details
                            .status === TStatus.PAID
                            ? 'success'
                            : financeAppState.selectedFinanceItem.basic_details
                              .status === TStatus.PARTIAL
                              ? 'warning'
                              : financeAppState.selectedFinanceItem
                                .basic_details.status === TStatus.UNPAID
                                ? 'error'
                                : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box data-testid="detail-payment-mode" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.columns.paymentMode')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .payment_mode || translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-currency" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.currency')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .currency_code || translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  <Box data-testid="detail-academic-year" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.list.academicYear')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financeAppState.selectedFinanceItem.basic_details
                        .academic_year ?? translateText('finance.list.nA')}
                    </Typography>
                  </Box>
                  {financeAppState.selectedFinanceItem.basic_details
                    .description && (
                      <Box
                        data-testid="detail-description"
                        sx={{ minWidth: 0, gridColumn: { sm: '1 / -1' } }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.list.description')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {
                            financeAppState.selectedFinanceItem.basic_details
                              .description
                          }
                        </Typography>
                      </Box>
                    )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

      {viewType === EFinanceView.LIST && (
        <Grid
          item
          xs={12}
          data-testid="finance-list-grid-wrapper"
          sx={{
            height: 'calc(100vh - 250px)',
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            data-testid="finance-list-grid"
            rows={filteredFinanceList}
            columns={getFinanceGridColumns()}
            onRowClick={handleRowClick}
            getRowId={(row) => {
              if (!row || !row.id) {
                console.warn('Invalid row data:', row);
                return '';
              }
              return row.id;
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FinanceListGrid;
